import {
  API,
  deleteRequest,
  getRequest,
  getRequestBlob,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api';
import { generateSearchParams } from '@/utils/generate-search-params';

import type {
  CreateTrashLogFormValues,
  TrashLog,
  TrashLogResponseDto,
  TrashLogsList,
  TrashLogsListDto,
  UpdateTrashLogFormValues,
} from './trash-log.dto';
import { mapCreateTrashLogDto, mapTrashLog, mapUpdateTrashLogDto } from './trash-log.mappers';

export const getTrashLogs = async (limit: number, page: number): Promise<TrashLogsList> => {
  const searchParams = generateSearchParams({ limit, page });
  const { data } = await getRequest<TrashLogsListDto>(API.VESSEL, `/trash-log?${searchParams}`);
  return {
    count: data.count,
    items: data.items.map(mapTrashLog),
  };
};

export const getTrashLogById = async (id: string): Promise<TrashLog> => {
  const { data } = await getRequest<TrashLogResponseDto>(API.VESSEL, `/trash-log/${id}`);
  return mapTrashLog(data);
};

const toRfc3339 = (date: string): string => {
  const d = new Date(date.length === 10 ? `${date}T00:00:00.000Z` : date);
  return Number.isNaN(d.getTime()) ? date : d.toISOString();
};

export const getTrashLogsByDate = async (date: string): Promise<TrashLog[]> => {
  const { data } = await getRequest<TrashLogResponseDto[] | undefined>(
    API.VESSEL,
    `/trash-log/date`,
    false,
    { date: toRfc3339(date) },
  );
  return (data ?? []).map(mapTrashLog);
};

export const createTrashLog = async (values: CreateTrashLogFormValues): Promise<TrashLog> => {
  const { data } = await postRequest<TrashLogResponseDto, unknown>(
    API.VESSEL,
    `/trash-log`,
    mapCreateTrashLogDto(values),
  );
  return mapTrashLog(data);
};

export const updateTrashLog = async (values: UpdateTrashLogFormValues): Promise<TrashLog> => {
  const { data } = await putRequest<TrashLogResponseDto, unknown>(
    API.VESSEL,
    `/trash-log/${values.id}`,
    mapUpdateTrashLogDto(values),
  );
  return mapTrashLog(data);
};

export const deleteTrashLog = async (id: string): Promise<void> => {
  await deleteRequest(API.VESSEL, `/trash-log/${id}`);
};

export const signTrashLog = async (id: string): Promise<TrashLog> => {
  const { data } = await patchRequest<TrashLogResponseDto, unknown>(
    API.VESSEL,
    `/trash-log/${id}/sign`,
    {},
  );
  return mapTrashLog(data);
};

export const getTrashLogPdf = async (id: string, timezone?: string): Promise<Blob> => {
  return getRequestBlob(API.VESSEL, `/trash-log/${id}/pdf`, { timezone });
};
